import { useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Navigate, NavigationType, Outlet, Route, Routes, useNavigationType } from 'react-router'

import {
  AuthorizedRoutes,
  ConnectionSetupRoutes,
  PublicRoutes,
  SettingsRoutes,
  SetupWorkspaceRoutes,
} from 'app/application/types'
import { BrowseConnections, Connection, Connections } from 'app/connections/presentation'
import {
  ConnectConnection,
  ConnectConnectionSettings,
  ConnectionSetupSteps,
} from 'app/connections/presentation/connect'
import {
  ManageConnection,
  ManageConnectionOverview,
  ManageConnectionSettings,
} from 'app/connections/presentation/manage'
import { Dashboard } from 'app/dashbaord/presentation'
import { CreateObjective, ObjectiveDetails, Objectives } from 'app/objectives/presentation'
import { ApiKeys, General, Members, Profile, Settings } from 'app/settings/presentation'
import { CreateSignal, NewSignal, SignalDetails, Signals } from 'app/signals/presentation'
import {
  CheckInbox,
  CreateAccess,
  CreateAccessStep,
  CreateNewPassword,
  LoginPage,
  ResetPassword,
  SessionExpired,
} from 'app/user/presentation'

import { useAuthorization } from '../useAuthorization'
import { ErrorPage } from './ErrorPage'
import { Page } from './Page'

const Router = () => {
  const { authorization, setAuthorization } = useAuthorization()
  const navigationType = useNavigationType()

  useEffect(() => {
    // reset expired authorization in case of reload or browser back
    if (authorization?.state === 'expired' && navigationType === NavigationType.Pop) {
      setAuthorization(null)
    }
  }, [authorization?.state, navigationType, setAuthorization])

  return (
    <Routes>
      <Route path={PublicRoutes.CreateWorkspace} element={<CreateAccess isCreateWorkspace />}>
        <Route index element={<CreateAccessStep.SetupWorkspace />} />
        <Route path={SetupWorkspaceRoutes.SignUp} element={<CreateAccessStep.SignUp />} />
        <Route path={SetupWorkspaceRoutes.Password} element={<CreateAccessStep.SetupPassword />} />
      </Route>
      <Route path={PublicRoutes.SignUp} element={<CreateAccess />}>
        <Route index element={<CreateAccessStep.SignUp />} />
        <Route path={SetupWorkspaceRoutes.Password} element={<CreateAccessStep.SetupPassword />} />
      </Route>
      <Route path={PublicRoutes.Login}>
        <Route index element={<LoginPage />} />
      </Route>
      <Route path={PublicRoutes.ResetPassword}>
        <Route index element={<ResetPassword />} />
        <Route path={SetupWorkspaceRoutes.CheckInbox} element={<CheckInbox />} />
      </Route>
      <Route path={PublicRoutes.CreateNewPassword} element={<CreateNewPassword />} />
      <Route path={PublicRoutes.SessionExpired} element={<SessionExpired />} />

      <Route
        path="/"
        element={
          authorization ? (
            // check for valid, to force a logout as this prop is new and not available in former application
            authorization.state === 'valid' ? (
              <ErrorBoundary FallbackComponent={ErrorPage}>
                <Outlet />
              </ErrorBoundary>
            ) : (
              <Navigate to={PublicRoutes.SessionExpired} replace />
            )
          ) : (
            <Navigate to={PublicRoutes.Login} replace />
          )
        }
      >
        <Route path={AuthorizedRoutes.Settings} element={<Settings />}>
          <Route index element={<Navigate to={SettingsRoutes.General} replace />} />
          <Route path={SettingsRoutes.General} element={<General />} />
          <Route path={SettingsRoutes.ApiKeys} element={<ApiKeys />} />
          <Route
            path={SettingsRoutes.Members}
            element={
              authorization?.isAdmin ? (
                <Members />
              ) : (
                <Navigate to={`../${SettingsRoutes.General}`} replace />
              )
            }
          />
          <Route path={SettingsRoutes.Profile} element={<Profile />} />
          <Route
            path="workspace"
            element={<Navigate to={`../${SettingsRoutes.General}`} replace />}
          />
          <Route path="users" element={<Navigate to={`../${SettingsRoutes.Members}`} replace />} />
          <Route path="access" element={<Navigate to={`../${SettingsRoutes.ApiKeys}`} replace />} />
          <Route path="user" element={<Navigate to={`../${SettingsRoutes.Profile}`} replace />} />
        </Route>
        <Route element={<Page />}>
          <Route index element={<Dashboard />} />
          <Route path="connections" element={<Connections />} />
          <Route path="connections/browse" element={<BrowseConnections />} />
          <Route path="connections/:name" element={<Connection />} />
          <Route path="objectives" element={<Objectives />} />
          <Route path="objectives/create" element={<CreateObjective />} />
          <Route path="objectives/:id" element={<ObjectiveDetails />} />
          <Route path={AuthorizedRoutes.ConnectConnection} element={<ConnectConnection />}>
            <Route index element={<ConnectConnectionSettings />} />
            <Route path={ConnectionSetupRoutes.SignIn} element={<ConnectionSetupSteps.SignIn />} />
            <Route
              path={ConnectionSetupRoutes.SelectAccounts}
              element={<ConnectionSetupSteps.SelectAccounts />}
            />
            <Route element={<ConnectionSetupSteps.StepWithConnectionAndAccounts />}>
              <Route
                path={ConnectionSetupRoutes.ConnectionVerified}
                element={<ConnectionSetupSteps.ConnectionVerified />}
              />
              <Route
                path={ConnectionSetupRoutes.Settings}
                element={<ConnectionSetupSteps.Settings />}
              />
            </Route>
          </Route>
          <Route path={AuthorizedRoutes.ManageConnection} element={<ManageConnection />}>
            <Route index element={<ManageConnectionOverview />} />
            <Route path="settings" element={<ManageConnectionSettings />} />
          </Route>
          <Route path="signals" element={<Signals />} />
          <Route path="signals/:id" element={<SignalDetails />} />
          <Route path="signals/new" element={<NewSignal />} />
          <Route path="signals/new/:platform/:signalType/:variant?" element={<CreateSignal />} />
        </Route>
        <Route path="*" element={<Navigate to="/" />} />
      </Route>
    </Routes>
  )
}

export default Router
